import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const openFile = async (aid) => {
  try { const r = await api.get(`/attachments/${aid}/view`, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); }
  catch { toast.error("Could not open photo"); }
};

function Item({ it, refs }) {
  const fail = it.result === "Fail";
  const photos = refs.filter((p) => p.field_id === `${it.id}_photo`);
  return (
    <div className={`rounded-lg px-3 py-2 text-sm ${fail ? "bg-rose-50 border border-rose-200" : "border border-slate-100"}`} data-testid={`insp-item-${it.id}`}>
      <div className="flex justify-between gap-3"><span>{it.label}</span><b className={fail ? "text-rose-700" : it.result === "Pass" ? "text-emerald-700" : "text-slate-500"}>{it.result}</b></div>
      {it.defect && <p className="text-rose-800 mt-1">Defect: {it.defect}</p>}
      {it.comments && <p className="text-slate-600 mt-1">Comments: {it.comments}</p>}
      {photos.map((p) => <button key={p.attachment_id} className="text-indigo-600 underline text-xs mr-2" onClick={() => openFile(p.attachment_id)}>View photo</button>)}
    </div>
  );
}

function Signature({ v }) {
  if (!v) return <span className="text-slate-400">Not signed</span>;
  return String(v).startsWith("data:image/") ? <img src={v} alt="Signature" className="h-16 bg-white border rounded" /> : <span className="italic">{v}</span>;
}

function Review({ d, onDone }) {
  const [txt, setTxt] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async (safe) => {
    setBusy(true);
    try { const r = await api.post(`/forklift-checks/${d.id}/review`, { action_taken: txt, mark_safe: safe }); toast.success(safe ? (r.data.status_changed ? "Signed off — forklift back in use" : "Review recorded — a newer defect on this forklift is still open") : "Review recorded"); setTxt(""); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || "Could not save"); }
    setBusy(false);
  };
  return (
    <div className="space-y-2" data-testid="forklift-review-form">
      <Textarea value={txt} onChange={(e) => setTxt(e.target.value)} placeholder="What was done to rectify the defect?" data-testid="forklift-review-text" />
      <div className="flex gap-2">
        <Button variant="outline" disabled={busy || txt.length < 3} onClick={() => save(false)} data-testid="forklift-review-save">Record review (keep Do Not Use)</Button>
        <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" disabled={busy || txt.length < 3} onClick={() => save(true)} data-testid="forklift-review-safe">Rectified — mark safe to use</Button>
      </div>
    </div>
  );
}

export function ForkliftInspectionDialog({ id, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const load = useCallback(() => { api.get(`/forklift-checks/${id}`).then((r) => setD(r.data)).catch(() => toast.error("Could not load inspection")); }, [id]);
  useEffect(() => { load(); }, [load]);
  const a = d?.submission?.answers || {};
  const refs = d?.submission?.attachment_refs || [];
  return (
    <Dialog open onOpenChange={(o) => { if (!o) { onClose(); } }}>
      <DialogContent className="max-w-2xl max-h-[95vh] overflow-y-auto" data-testid="forklift-inspection-dialog">
        <DialogHeader><DialogTitle>{d ? `${d.forklift} · ${d.inspection_date} ${d.inspection_time || ""}` : "Loading…"}</DialogTitle></DialogHeader>
        {d && (
          <div className="space-y-4 text-sm">
            <p>Inspector: <b>{d.inspector}</b> · Make/Model: {d.model || "—"} · Result: <b className={d.result === "passed" ? "text-emerald-700" : "text-rose-700"} data-testid="insp-result">{d.result === "passed" ? "Passed" : `Defects Reported (${d.fail_count})`}</b></p>
            <div className="space-y-1.5">{d.items.map((it) => <Item key={it.id} it={it} refs={refs} />)}</div>
            {d.defects_notes && <p><b>Defects found:</b> {d.defects_notes}</p>}
            <div className="flex items-center gap-3"><b>Operator signature:</b> <Signature v={a.flt_sig} /> <span className="text-slate-500">{a.flt_sig_date}</span></div>
            <p className="text-xs text-slate-400">Form version {d.submission?.form_version || "—"} · submitted {d.submission?.submitted_at} by {d.submitted_by}. The original inspection can't be edited.</p>
            {d.result === "defects_reported" && (
              <div className="border-t pt-3 space-y-2">
                <h3 className="font-bold">Manager review & rectification</h3>
                {d.reviews.map((r) => <p key={r.id} className="text-sm" data-testid={`forklift-review-${r.id}`}><b>{r.reviewed_by}</b> · {r.created_at.slice(0, 16).replace("T", " ")}: {r.action_taken} {r.mark_safe ? "— marked safe" : ""}</p>)}
                {d.can_review && !d.reviews.some((r) => r.mark_safe) && <Review d={d} onDone={() => { load(); onChanged(); }} />}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
